import { Reservation, Task } from "~/packages/taskrouter";
import { CreateCbmTaskResponse, CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { isWorkerOffline, sendTrackingEvent } from "../../ActionUtils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { PreviewOutboundServiceImpl } from "~/backend/generated/PreviewFlex/api/previewOutbound.service";
import { OutboundWorkflowAndQueueResponse } from "~/backend/generated/PreviewFlex/model/outboundWorkflowAndQueueResponse";
import { OutboundAddressConfiguration } from "~/backend/generated/PreviewFlex/model/outboundAddressConfiguration";
import { OutboundTaskHandler } from "~/modules/actions/OutboundTaskHandler";
import { Conversation, ConversationImpl } from "~/modules/Conversation";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";




export interface StartOutboundEmailTaskOptions {
    from?: string;
    fromName?: string;
    taskQueueSid?: string;
    workflowSid?: string;
    attributesForTaskCreation?: object;
}




export interface StartOutboundEmailTaskResponse {
    task: Task;
    conversation: Conversation;
}



















export class StartOutboundEmailTask implements Action<Promise<StartOutboundEmailTaskResponse>> {
    


    #to: string;

    


    #options?: StartOutboundEmailTaskOptions;

    constructor(to: string, options?: StartOutboundEmailTaskOptions) {
        this.#to = to;
        this.#options = options;
    }

    async run(ctx: {}): Promise<StartOutboundEmailTaskResponse> {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        const conversations = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const previewOutboundServiceImpl = context.retrieveInstanceOf(
            ContextInstance.PreviewOutboundServiceImpl,
            PreviewOutboundServiceImpl
        );

        await cbm.init();
        await taskRouter.init();
        const conversationInitPromise = conversations.init();

        const logger = getLogger(context)("StartOutboundEmailTask");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`StartOutboundEmailTask invoked to address: ${this.#to}`);

        const worker = taskRouter.worker;
        const { attributesForTaskCreation } = this.#options || {};

        if (!worker) {
            const errorMsg = `StartOutboundEmailTask: worker is not initialized`;
            logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskrouterOfflineActivitySid = accountConfig.get().taskrouterOfflineActivitySid;
        if (!taskrouterOfflineActivitySid) {
            const errorMsg = "StartOutboundEmailTask: taskrouterOfflineActivitySid is undefined";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (isWorkerOffline(worker, taskrouterOfflineActivitySid)) {
            const errorMsg = `StartOutboundEmailTask: worker is offline, outbound email task cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!this.#to) {
            const errorMsg = `StartOutboundEmailTask: to is a required parameter`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        let outboundWorkflowAndQueue: OutboundWorkflowAndQueueResponse | undefined;
        if (!this.#options?.workflowSid || !this.#options?.taskQueueSid) {
            try {
                outboundWorkflowAndQueue = await previewOutboundServiceImpl.getOutboundWorkflowAndQueue(
                    accountConfig.get().accountSid
                );
            } catch (error) {
                const errorMsg =
                    `StartOutboundEmailTask: failed to get outbound email workflow settings, ` +
                    `make sure Email settings are configured in Twilio Console`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        const workflowSid = this.#options?.workflowSid || outboundWorkflowAndQueue?.email?.workflow_sid;
        if (!workflowSid) {
            const errorMsg =
                `StartOutboundEmailTask: outbound email workflow is not configured in Twilio Console. ` +
                `Please configure or provide 'workflowSid' in options`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskQueueSid = this.#options?.taskQueueSid || outboundWorkflowAndQueue?.email?.queue_sid;
        if (!taskQueueSid) {
            const errorMsg =
                `StartOutboundEmailTask: outbound email queue is not configured in Twilio Console. ` +
                `Please configure or provide 'taskQueueSid' in options`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        let outboundAddressConfiguration: OutboundAddressConfiguration | undefined;
        if (!this.#options?.from) {
            try {
                outboundAddressConfiguration = await previewOutboundServiceImpl.getOutboundAddressConfiguration(
                    accountConfig.get().accountSid
                );
            } catch (error) {
                const errorMsg =
                    `StartOutboundEmailTask: failed to get outbound email address settings, ` +
                    `make sure Email settings are configured in Twilio Console`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                    error: errorMsg,
                    workflowSid,
                    taskQueueSid
                });
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        const fromParam = this.#options?.from || outboundAddressConfiguration?.email?.coordinates?.address;
        if (!fromParam) {
            const errorMsg =
                `StartOutboundEmailTask: default outbound email address is not configured in Twilio Console. ` +
                `Please configure or provide 'from' in options`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskQueueSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const outboundAttributes = { ...attributesForTaskCreation, direction: "outbound" };

        const outboundTaskHandler = new OutboundTaskHandler(worker);
        outboundTaskHandler.startListeningReservations();

        let createCbmTaskResponse: CreateCbmTaskResponse;
        try {
            createCbmTaskResponse = await cbm.createEmailTask(worker, workflowSid, taskQueueSid, {
                attributes: { customerAddress: this.#to, ...outboundAttributes },
                to: this.#to,
                from: fromParam!,
                fromName: this.#options?.fromName,
                taskUniqueChannelName: "email"
            });
        } catch (error) {
            const errorMsg = `StartOutboundEmailTask: failed to create outbound email task`;
            outboundTaskHandler.stopListeningReservations();
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskQueueSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        let reservation: Reservation;
        try {
            reservation = await outboundTaskHandler.waitForTaskReservation(createCbmTaskResponse.taskSid);
        } catch (error) {
            const errorMsg = `StartOutboundEmailTask: failed to create reservation for task ${createCbmTaskResponse.taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskQueueSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        try {
            reservation = await cbm.acceptReservation(reservation);
        } catch (error) {
            const errorMsg = `StartOutboundEmailTask: Failed to accept reservation ${reservation.sid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        let conversation: Conversation;
        const conversationSid = reservation.task.attributes.conversationSid;
        try {
            await conversationInitPromise;
            const twilioConversation = await conversations.getConversationBySid(conversationSid);
            conversation = new ConversationImpl(context, twilioConversation);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                workflowSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid,
                reservationSid: reservation.sid,
                conversationSid
            });
        } catch (error) {
            const errorMsg = `StartOutboundEmailTask: Failed to fetch conversation ${conversationSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid,
                reservationSid: reservation.sid,
                conversationSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        return { task: reservation.task, conversation };
    }
}
